import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  username: string;
  password: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  currentUser: string | null;
  login: (username: string, password: string, recaptchaToken?: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  failedAttempts: number;
  resetFailedAttempts: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [failedAttempts, setFailedAttempts] = useState<number>(0);

  // Load users from environment variables
  const getUsers = (): User[] => {
    try {
      const usersJson = process.env.REACT_APP_AUTH_USERS || '[]';
      return JSON.parse(usersJson);
    } catch (error) {
      console.error('Failed to parse REACT_APP_AUTH_USERS:', error);
      return [];
    }
  };

  // Simple hash function for password comparison
  const simpleHash = (str: string): string => {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString();
  };

  // Load failed attempts from localStorage
  const loadFailedAttempts = (): number => {
    try {
      const attempts = localStorage.getItem('ras_failed_attempts');
      return attempts ? parseInt(attempts, 10) : 0;
    } catch (error) {
      console.error('Failed to load failed attempts:', error);
      return 0;
    }
  };

  // Save failed attempts to localStorage
  const saveFailedAttempts = (attempts: number) => {
    try {
      localStorage.setItem('ras_failed_attempts', attempts.toString());
      setFailedAttempts(attempts);
    } catch (error) {
      console.error('Failed to save failed attempts:', error);
    }
  };

  // Reset failed attempts
  const resetFailedAttempts = () => {
    localStorage.removeItem('ras_failed_attempts');
    setFailedAttempts(0);
  };

  // Check if user is already authenticated on app load
  useEffect(() => {
    const savedAuth = localStorage.getItem('ras_auth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        const { username, sessionHash, timestamp } = authData;
        
        // Check if session is still valid (24 hours)
        const currentTime = new Date().getTime();
        const sessionAge = currentTime - timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours

        if (sessionAge < maxAge && username && sessionHash) {
          setIsAuthenticated(true);
          setCurrentUser(username);
        } else {
          // Session expired, clear storage
          localStorage.removeItem('ras_auth');
        }
      } catch (error) {
        console.error('Failed to parse saved auth data:', error);
        localStorage.removeItem('ras_auth');
      }
    }
    
    // Load failed attempts from localStorage
    setFailedAttempts(loadFailedAttempts());
    setLoading(false);
  }, []);

  const login = async (username: string, password: string, recaptchaToken?: string): Promise<boolean> => {
    const users = getUsers();
    const user = users.find(u => u.username === username && u.password === password);
    
    // Get reCAPTCHA configuration from environment
    const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true';
    const triggerAttempts = parseInt(process.env.REACT_APP_RECAPTCHA_TRIGGER_ATTEMPTS || '3', 10);
    
    // Check if reCAPTCHA is required
    if (recaptchaEnabled && failedAttempts >= triggerAttempts && !recaptchaToken) {
      return false; // reCAPTCHA required but not provided
    }
    
    if (user) {
      setIsAuthenticated(true);
      setCurrentUser(username);
      
      // Reset failed attempts on successful login
      resetFailedAttempts();
      
      // Save session to localStorage
      const sessionData = {
        username,
        sessionHash: simpleHash(username + password + Date.now()),
        timestamp: new Date().getTime()
      };
      localStorage.setItem('ras_auth', JSON.stringify(sessionData));
      
      return true;
    }
    
    // Increment failed attempts on failed login
    saveFailedAttempts(failedAttempts + 1);
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    localStorage.removeItem('ras_auth');
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      currentUser,
      login,
      logout,
      loading,
      failedAttempts,
      resetFailedAttempts
    }}>
      {children}
    </AuthContext.Provider>
  );
};